import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import { rd } from '@passionware/monads';
import { Resource } from '__types__';
import { debounce } from 'lodash';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  checkContactDuplicates,
  clearContactDuplicates
} from 'store/v1/contacts/contacts.actions.js';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import type { ResourceMembership } from 'v5/core/entity-schema/entitySchema.types';
import { usesSingleNameField } from 'v5/core/entity-schema/resource/fieldSpecs';
import { Skeleton } from 'v5/design-system/Skeleton';
import { createScopeSectionIconRenderer } from 'v5/features/core/enterprise/renderScopeSectionIcon';
import { WorkspaceMembershipPicker } from 'v5/features/resources/WorkspaceMembershipPicker';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import {
  ENTITY_CREATE_STEPS,
  type EntityCreateStep
} from 'v5/patterns/forms/dialogs/EntityCreateDialog';
import { ImageUploadField } from 'v5/patterns/forms/fields/image-upload/ImageUploadField';
import { SCHEMA_FORM_GUTTER } from 'v5/patterns/forms/schema-form/gutter';
import { SchemaForm } from 'v5/patterns/forms/schema-form/SchemaForm';
import type {
  CreateFormValues,
  SchemaFormHandle
} from 'v5/patterns/forms/schema-form/schemaForm.types';
import { ResourceTypeSelect } from 'v5/patterns/resource/ResourceTypeSelect';
import { Label } from 'v5/design-system/Label';
import { cn } from 'v5/platform/dom/cn';
import { AnimatedStage, AnimatedStages } from 'v5/primitives/AnimatedStages';
import { entityInitials } from 'v5/patterns/widgets/entityInitials';
import { createValuesToPatches, initialCreateValues } from './createFormValues';
import { newListItemValues } from './resourceBindings';
import {
  CreateDraft,
  ResourceFormServices,
  useResourceFormModel
} from './useResourceFormModel';
import { renderResourceUnitIcon } from './resourceUnitIcon';

export type ResourceCreateFormHandle = {
  /** Resolves with the created resource; rejects when validation or the request fails. */
  submit(): Promise<Resource>;
};

export interface ResourceCreateFormProps extends ResourceFormServices {
  /**
   * Step-1 choices, owned by the host (the dialog also shows them in its
   * pinned recap bar), so the form is controlled.
   */
  draft: CreateDraft;
  /** Takes an update: an upload may finish after the draft changed. */
  onDraftChange(update: (draft: CreateDraft) => CreateDraft): void;
  /**
   * Wizard step: `setup` = resource type and visibility, `form` = the schema
   * form. Both blocks stay mounted and slide between each other, so values
   * survive going back and forth. `undefined` renders both at once.
   */
  step?: EntityCreateStep;
  onStateChange?(state: {
    isSubmitting: boolean;
    isValid: boolean;
    isDirty: boolean;
  }): void;
  /** ⌘/Ctrl+Enter inside the form; the host runs its save flow. */
  onSubmitRequest?(): void;
  /** Typed full name, for the host's duplicate warning. */
  onNameChange?(name: string): void;
}

/**
 * Create-mode resource form: resource type, profile picture and membership
 * above a single `SchemaForm` whose sections follow the picked membership.
 * Submits one `POST /contacts` through `ResourceMutationService.create`.
 */
export const ResourceCreateForm = forwardRef<
  ResourceCreateFormHandle,
  ResourceCreateFormProps
>(function ResourceCreateForm(
  {
    draft,
    onDraftChange,
    step,
    onStateChange,
    onSubmitRequest,
    onNameChange,
    services
  },
  ref
) {
  const dispatch = useDispatch();
  const formRef = useRef<SchemaFormHandle<ResourceKinds>>(null);
  const lastValues = useRef<CreateFormValues<ResourceKinds> | null>(null);
  const createdRef = useRef<Resource | null>(null);

  const modelInput = useMemo(
    () => ({ mode: 'create' as const, draft, form: formRef }),
    [draft]
  );
  const model = useResourceFormModel(modelInput, services);
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const organizationId = rd.tryMap(scopeRd, scope => scope.organizationId);
  const identity = rd.tryMap(scopeRd, appScopeUtils.identityFromResolved);
  const resourceTypesRd =
    services.resourceTypeService.useResourceTypes(identity);
  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId,
    'active'
  );

  // Locations, organizations and items carry the whole name in `first_name`,
  // so for them a complete name is the first name alone.
  const resourceTypeModel =
    rd.tryMap(resourceTypesRd, resourceTypes =>
      resourceTypes.find(type => type.id === draft.resourceTypeId)
    )?.model ?? 'INDIVIDUAL';
  const singleNameField = usesSingleNameField(resourceTypeModel);

  const checkDuplicates = useMemo(
    () =>
      debounce((values: { first_name: string; last_name: string }) => {
        const firstName = values.first_name.trim();
        const lastName = values.last_name.trim();
        // The check is `/search/contacts`, a substring match over names: half a
        // name matches half the workspace. Ask only once the name is complete,
        // the same gate the resource pane has always used.
        if (firstName === '' || (!singleNameField && lastName === '')) {
          // A name taken back apart must not leave its matches behind.
          dispatch(clearContactDuplicates());
          return;
        }
        dispatch(
          checkContactDuplicates({
            first_name: firstName,
            last_name: lastName,
            resource_type_id: draft.resourceTypeId
          })
        );
      }, 300),
    [dispatch, draft.resourceTypeId, singleNameField]
  );
  // A check still waiting must not land after the dialog is gone.
  useEffect(() => () => checkDuplicates.cancel(), [checkDuplicates]);

  const { t } = useTranslation('resources', {
    keyPrefix: 'ResourceCreateForm'
  });
  // Typed name, for the picture placeholder initials.
  const [previewName, setPreviewName] = useState('');
  // An upload in flight counts as submitting: the dialog then holds Back and
  // Save, so the picture neither lands on a changed draft nor goes missing.
  const [isUploading, setIsUploading] = useState(false);
  const [schemaState, setSchemaState] = useState({
    isSubmitting: false,
    isValid: true,
    isDirty: false
  });
  // The host passes a fresh callback on every render; only the state matters.
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  useEffect(() => {
    onStateChangeRef.current?.({
      ...schemaState,
      isSubmitting: schemaState.isSubmitting || isUploading
    });
  }, [schemaState, isUploading]);

  const handleValuesChange = useCallback(
    (values: CreateFormValues<ResourceKinds>) => {
      lastValues.current = values;
      const nameUnit = values['global/core:full_name'];
      if (nameUnit && nameUnit.type === 'fields') {
        const name = nameUnit.values['core:full_name'];
        if (
          name &&
          typeof name === 'object' &&
          'first_name' in name &&
          'last_name' in name
        ) {
          const first = String(name.first_name ?? '');
          const last = String(name.last_name ?? '');
          const fullName = `${first} ${last}`.trim();
          setPreviewName(fullName);
          onNameChange?.(fullName);
          checkDuplicates({ first_name: first, last_name: last });
        }
      }
    },
    [checkDuplicates, onNameChange]
  );

  // The footer's warning goes with it on Back; the same name finds it again.
  useEffect(() => {
    if (step === 'form' && lastValues.current) {
      handleValuesChange(lastValues.current);
    }
  }, [step, handleValuesChange]);

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (!formRef.current) {
          throw new Error('The form is not ready yet');
        }
        createdRef.current = null;
        await formRef.current.submit();
        if (!createdRef.current) {
          throw new Error('Please fix the highlighted fields');
        }
        return createdRef.current;
      }
    }),
    []
  );

  // Step 1 needs only the resource types and workspaces, which have their own loading states.
  // Waiting on the whole form model would blank it on open and on every resource type change.
  const setupBlock = (
    <div
      className="flex flex-col gap-6 px-6 py-3"
      data-testid="resource-create-details"
    >
      <div className="flex flex-col gap-2">
        <Label>Resource type</Label>
        {rd
          .journey(resourceTypesRd)
          .wait(<Skeleton className="h-12 w-full" />)
          .catch(renderError)
          .map(resourceTypes => (
            <ResourceTypeSelect
              value={draft.resourceTypeId}
              resourceTypes={resourceTypes}
              onValueChange={resourceTypeId =>
                onDraftChange(draft => ({ ...draft, resourceTypeId }))
              }
            />
          ))}
      </div>
      {/* Without workspaces there is no visibility to pick. */}
      {rd.tryMap(workspacesRd, workspaces => workspaces.length === 0) !==
        true && (
        <WorkspaceMembershipPicker
          idPrefix="resource-create-membership"
          value={draft.membership}
          workspaces={workspacesRd}
          onChange={(membership: ResourceMembership) =>
            onDraftChange(draft => ({ ...draft, membership }))
          }
        />
      )}
    </div>
  );
  const formBlock = rd
    .journey(model)
    .wait(
      <div className="px-6 py-3">
        <Skeleton className="h-64 w-full" />
      </div>
    )
    .catch(error => <div className="px-6 py-3">{renderError(error)}</div>)
    .map(model => {
      if (model.mode !== 'create') return null;
      const { schema, kit, codecs, writeModel, workspaceIcons } = model;
      return (
        <div
          className={cn('flex flex-col gap-8', SCHEMA_FORM_GUTTER.lg)}
          data-testid="resource-create-fields"
        >
          <SchemaForm
            handle={formRef}
            schema={schema}
            kit={kit}
            navigation="tabs"
            initialValues={initialCreateValues(schema, codecs)}
            newListItem={(unitId, optionId) => {
              for (const section of schema.sections) {
                for (const unit of section.units) {
                  if (unit.id === unitId && unit.type === 'list') {
                    return newListItemValues(unit, optionId, codecs);
                  }
                }
              }
              throw new Error(`Unknown list unit ${unitId}`);
            }}
            onStateChange={setSchemaState}
            onValuesChange={handleValuesChange}
            onSubmitRequest={onSubmitRequest}
            onSubmit={async values => {
              const patches = createValuesToPatches(
                schema,
                values,
                codecs,
                draft.resourceTypeId
              );
              createdRef.current = await writeModel.create({
                resourceTypeId: draft.resourceTypeId,
                profilePicture: draft.profilePicture,
                membership: draft.membership,
                patches
              });
            }}
            variant="regular"
            size="md"
            renderSectionPrefix={section =>
              section.scope.type === 'global' ? (
                <div className="flex flex-col gap-2">
                  <Label className="font-semibold text-fg-default">
                    {t('profilePicture')}
                  </Label>
                  <ImageUploadField
                    services={services}
                    value={draft.profilePicture}
                    onChange={profilePicture =>
                      onDraftChange(draft => ({ ...draft, profilePicture }))
                    }
                    onUploadingChange={setIsUploading}
                    placeholder={entityInitials(previewName) || undefined}
                  />
                </div>
              ) : null
            }
            renderSectionIcon={createScopeSectionIconRenderer(workspaceIcons)}
            renderUnitIcon={renderResourceUnitIcon}
          />
        </div>
      );
    });

  if (step === undefined) {
    return (
      <div className="flex flex-col gap-8">
        {setupBlock}
        {formBlock}
      </div>
    );
  }
  return (
    // Both steps span the unpadded dialog body; clip at its edges.
    <AnimatedStages stage={step} order={ENTITY_CREATE_STEPS}>
      <AnimatedStage stage="setup">{setupBlock}</AnimatedStage>
      <AnimatedStage stage="form">{formBlock}</AnimatedStage>
    </AnimatedStages>
  );
});
